import { useState } from "react";
import { X, Send, ChevronDown } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { parsePhoneNumber, isValidPhoneNumber } from "libphonenumber-js";

const FluencyChallengeInterestForm = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    languageLearning: "",
    languageLevel: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const languageOptions = [
    { value: "English", label: t("fluencyChallenge.form.languages.english") },
    { value: "Spanish", label: t("fluencyChallenge.form.languages.spanish") },
  ];
  const levelOptions = [
    { value: "Beginner", label: t("fluencyChallenge.form.levels.beginner") },
    {
      value: "Intermediate",
      label: t("fluencyChallenge.form.levels.intermediate"),
    },
    { value: "Advanced", label: t("fluencyChallenge.form.levels.advanced") },
  ];

  const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const MAX_PHONE_LENGTH = 20;
  const PHONE_ERROR_MSG =
    t("fluencyChallenge.form.errors.invalidPhoneWithCountryCode") ||
    "Please enter a valid phone number with country code (e.g., +1 for US, +44 for UK)";

  const validateEmail = (emailValue) => EMAIL_REGEX.test(emailValue);

  const validatePhoneNumber = (phoneValue, forceValidation = false) => {
    if (!phoneValue?.trim()) {
      setPhoneError("");
      return false;
    }
    if (!forceValidation && phoneValue.trim().length < 7) {
      setPhoneError("");
      return false;
    }
    try {
      if (isValidPhoneNumber(phoneValue)) {
        setPhoneError("");
        return true;
      }
      setPhoneError(PHONE_ERROR_MSG);
      return false;
    } catch {
      setPhoneError(PHONE_ERROR_MSG);
      return false;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === "email" && emailError) setEmailError("");
    if (name === "phone") validatePhoneNumber(value);
  };

  const handlePhoneChange = (e) => {
    let value = e.target.value;
    if (!value) {
      setFormData((prev) => ({ ...prev, phone: "" }));
      setPhoneError("");
      return;
    }
    if (value.startsWith("+")) {
      value = "+" + value.slice(1).replace(/[^\d\s\-()]/g, "");
    } else {
      value = value.replace(/[^\d\s\-()+]/g, "");
      if (value && !value.startsWith("+") && /^\d/.test(value))
        value = "+" + value;
    }
    if (value.length > MAX_PHONE_LENGTH)
      value = value.slice(0, MAX_PHONE_LENGTH);
    setFormData((prev) => ({ ...prev, phone: value }));
    validatePhoneNumber(value);
  };

  const handleEmailBlur = () => {
    setEmailError(
      formData.email && !validateEmail(formData.email)
        ? t("fluencyChallenge.form.errors.invalidEmail")
        : "",
    );
  };

  const handlePhoneBlur = () => {
    if (formData.phone.trim()) validatePhoneNumber(formData.phone, true);
  };

  const formatPhoneNumber = (phone) => {
    try {
      if (isValidPhoneNumber(phone)) {
        return parsePhoneNumber(phone).formatInternational();
      }
    } catch {
      console.warn("Could not parse phone number, using original");
    }
    return phone.trim();
  };

  const resetForm = () => {
    setFormData({
      fullName: "",
      email: "",
      phone: "",
      languageLearning: "",
      languageLevel: "",
    });
    setEmailError("");
    setPhoneError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateEmail(formData.email)) {
      setEmailError(t("fluencyChallenge.form.errors.invalidEmail"));
      return;
    }
    if (!validatePhoneNumber(formData.phone, true)) {
      toast.error(PHONE_ERROR_MSG);
      return;
    }

    setIsSubmitting(true);
    try {
      await fetch(
        "https://script.google.com/macros/s/AKfycbzLClHLlYIAZezOkIwUJ6ykZ9ozD01Oo0Ll0FsPJbMBTCG9IgDZeeIYHRdJgFJ9XVH_uA/exec",
        {
          method: "POST",
          mode: "no-cors",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            phone: formatPhoneNumber(formData.phone),
            formType: "Fluency Challenge",
          }),
        },
      );
      onClose();
      resetForm();
      navigate("/7-day-success");
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        t("fluencyChallenge.form.errors.submitError") ||
          "There was an error submitting the form. Please try again or contact us directly.",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const inputBaseClass =
    "w-full rounded-xl border-2 px-4 py-3 text-[#042F0C] transition-all focus:outline-none focus:ring-2";
  const inputValidClass =
    "border-[#14B82C]/30 focus:border-[#14B82C] focus:ring-[#14B82C]/20";
  const inputErrorClass =
    "border-red-500 focus:border-red-500 focus:ring-red-500/20";
  const selectClass =
    "w-full appearance-none rounded-xl border-2 border-[#14B82C]/30 bg-white px-4 py-3 pr-12 text-[#042F0C] transition-all focus:border-[#14B82C] focus:outline-none focus:ring-2 focus:ring-[#14B82C]/20";

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative mx-4 max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-gradient-to-br from-[#F0FDF1] to-[#E6FDE9] p-8 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-6 top-6 flex items-center justify-center rounded-full bg-white p-2 text-gray-600 shadow-lg transition-all hover:bg-gray-50 hover:shadow-xl"
          aria-label="Close"
        >
          <X className="h-6 w-6" />
        </button>

        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-[#14B82C] p-4 shadow-lg">
              <Send className="h-8 w-8 text-white" />
            </div>
          </div>
          <h2 className="mb-4 text-4xl font-bold text-[#042F0C]">
            {t("fluencyChallenge.form.title")}
          </h2>
          <p className="text-xl leading-relaxed text-[#3d3d3d]">
            {t("fluencyChallenge.form.description")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="rounded-2xl border border-[#14B82C]/20 bg-white p-8 shadow-lg">
            <div className="mb-6 flex items-center gap-3">
              <div className="rounded-full bg-[#FFBF00] p-2">
                <span className="text-lg font-bold text-[#042F0C]">1</span>
              </div>
              <h3 className="text-2xl font-bold text-[#042F0C]">
                {t("fluencyChallenge.form.section1")}
              </h3>
            </div>

            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div className="md:col-span-2">
                <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                  {t("fluencyChallenge.form.fullName")} *
                </label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  required
                  className={`${inputBaseClass} ${inputValidClass}`}
                  placeholder={t("fluencyChallenge.form.enterFullName")}
                />
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                  {t("fluencyChallenge.form.emailAddress")} *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={handleEmailBlur}
                  required
                  className={`${inputBaseClass} ${emailError ? inputErrorClass : inputValidClass}`}
                  placeholder={t("fluencyChallenge.form.enterEmail")}
                />
                {emailError && (
                  <p className="mt-2 text-sm text-red-600">{emailError}</p>
                )}
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                  {t("fluencyChallenge.form.phoneNumber")} *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  onBlur={handlePhoneBlur}
                  required
                  className={`${inputBaseClass} ${phoneError ? inputErrorClass : inputValidClass}`}
                  placeholder="+1 555-123-4567"
                  maxLength={MAX_PHONE_LENGTH}
                />
                {phoneError && (
                  <p className="mt-2 text-sm text-red-600">{phoneError}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                  {t("fluencyChallenge.form.phoneHelper") ||
                    "Include country code (e.g., +1 for US, +44 for UK)"}
                </p>
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                  {t("fluencyChallenge.form.languageLearning")} *
                </label>
                <div className="relative">
                  <select
                    name="languageLearning"
                    value={formData.languageLearning}
                    onChange={handleInputChange}
                    required
                    className={selectClass}
                  >
                    <option value="">
                      {t("fluencyChallenge.form.selectLanguage")}
                    </option>
                    {languageOptions.map((lang) => (
                      <option key={lang.value} value={lang.value}>
                        {lang.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                </div>
              </div>

              <div>
                <label className="mb-3 block text-sm font-semibold text-[#042F0C]">
                  {t("fluencyChallenge.form.languageLevel")} *
                </label>
                <div className="relative">
                  <select
                    name="languageLevel"
                    value={formData.languageLevel}
                    onChange={handleInputChange}
                    required
                    className={selectClass}
                  >
                    <option value="">
                      {t("fluencyChallenge.form.selectLevel")}
                    </option>
                    {levelOptions.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#3d3d3d]" />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex transform items-center gap-3 rounded-full px-12 py-4 text-xl font-bold transition-all hover:scale-105 ${
                isSubmitting
                  ? "cursor-not-allowed bg-gray-400 text-gray-600"
                  : "bg-gradient-to-r from-[#14B82C] to-[#0F9A1A] text-white shadow-lg hover:from-[#0F9A1A] hover:to-[#0D7A15] hover:shadow-xl"
              }`}
            >
              {isSubmitting ? (
                <>
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  {t("fluencyChallenge.form.submitting")}
                </>
              ) : (
                <>
                  <Send className="h-6 w-6" />
                  {t("fluencyChallenge.form.joinChallenge")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FluencyChallengeInterestForm;
